<script setup lang="ts">
import type { EnginePreference } from '@/core/types'

const model = defineModel<EnginePreference>({ required: true })

/**
 * Only two of the three preferences are offered. `layout` is the strict variant
 * — no fallback to anydoc — which is only useful for comparing the engines
 * against each other, not for converting a document you want to read.
 */
const OPTIONS: { id: EnginePreference; label: string; detail: string }[] = [
  {
    id: 'auto',
    label: 'Layout-aware',
    detail: 'Rebuilds reading order and paragraphs from the page geometry.',
  },
  {
    id: 'fast',
    label: 'Fast',
    detail: "Reads the PDF's text in the order the file stores it.",
  },
]
</script>

<template>
  <fieldset class="rounded-xl border border-slate-200 px-3 py-2.5 dark:border-slate-800">
    <legend class="px-1 text-xs font-semibold tracking-wide text-slate-500 uppercase">
      PDF engine
    </legend>

    <div class="flex gap-0.5 rounded-lg bg-slate-100 p-0.5 dark:bg-slate-800">
      <button
        v-for="option in OPTIONS"
        :key="option.id"
        type="button"
        :aria-pressed="model === option.id"
        :title="option.detail"
        class="flex-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500"
        :class="
          model === option.id
            ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-slate-100'
            : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
        "
        @click="model = option.id"
      >
        {{ option.label }}
      </button>
    </div>

    <p class="mt-1.5 text-xs leading-snug text-slate-500 dark:text-slate-400">
      {{ OPTIONS.find((option) => option.id === model)?.detail }}
      Other formats are unaffected.
    </p>
  </fieldset>
</template>
